import { useCallback, useEffect, useMemo, useState } from "react"
import { Helmet } from "react-helmet-async"
import Modal from "react-responsive-modal"
import BreadCrumbs from "../components/BreadCrumbs"
import CheckerTripCard from "../components/CheckerTripCard"
import CheckerPastTripCard from "../components/CheckerPastTripCard"
import CheckerPresetRow from "../components/CheckerPresetRow"
import CheckerHoursSlider from "../components/CheckerHoursSlider"
import CheckerCompanionPicker from "../components/CheckerCompanionPicker"
import CheckerPlacePickerField from "../components/CheckerPlacePickerField"
import CommunityActivityColumn from "../components/CommunityActivityColumn"
import CommunitySidebar from "../components/CommunitySidebar"
import {
    activateCheckerTrip,
    addCheckerCompanion,
    cancelCheckerTrip,
    createCheckerTrip,
    deleteCheckerTrip,
    extendCheckerTrip,
    getCheckerCompanions,
    getCheckerEvents,
    getCheckerTrip,
    getCheckerTrips,
    removeCheckerCompanion,
    submitCheckerCheckin,
    updateCheckerTrip,
} from "../services/checkerService"
import {
    activeCheckerCompanions,
    deriveCheckerStatus,
    formatDateTime,
    getInvitableCompanions,
    parseApiDateTime,
} from "../utilities/checker"
import { useCommunityManager } from "../hooks/useCommunityManager"
import "../css/Checker.css"

const CADENCE_PRESETS_CREATE = [15, 30, 60, 120].map((mins) => ({
    value: mins,
    label: `${mins}m`,
}))
// A couple of longer-cadence options beyond create's own presets -- an
// already-running trip is more likely to have started with a longer
// interval (e.g. a multi-day trip) than a brand-new one. Matches Android's
// EditTripScreen.
const CADENCE_PRESETS_EDIT = [15, 30, 60, 120, 240, 360].map((mins) => ({
    value: mins,
    label: `${mins}m`,
}))
const CADENCE_MODE_OPTIONS = [
    { value: "fixed", label: "Fixed Interval" },
    { value: "fibonacci", label: "Fibonacci" },
]
const GRACE_PRESETS = [5, 10, 15, 30].map((mins) => ({
    value: mins,
    label: `${mins}m`,
}))

// GCS-hosted image shown in place of an empty trip list. The Android app has
// its own (CHECKER_EMPTY_STATE_IMAGE_URL in CheckerScreen.kt).
const CHECKER_EMPTY_STATE_IMAGE_URL =
    "https://storage.googleapis.com/chaperon/media/images/rachel_in_paris%401024w.jpg"

function toArray(result) {
    if (Array.isArray(result)) return result
    if (Array.isArray(result?.data)) return result.data
    if (Array.isArray(result?.checkers)) return result.checkers
    if (Array.isArray(result?.trips)) return result.trips
    return []
}

function normalizeCheckerTrip(trip) {
    const { displayStatus } = deriveCheckerStatus(trip)

    return {
        ...trip,
        display_status: displayStatus,
        display_start_time: formatDateTime(trip?.start_time),
        display_end_time: formatDateTime(trip?.end_time),
        display_updated_at: formatDateTime(trip?.updated_at),
        display_created_at: formatDateTime(trip?.created_at),
    }
}

function statusClass(status) {
    switch (status) {
        case "active":
            return "checker-status-active"
        case "scheduled":
            return "checker-status-scheduled"
        case "danger":
            return "checker-status-danger"
        default:
            return "checker-status-neutral"
    }
}

function getUserIdFromStorage() {
    try {
        const raw = localStorage.getItem("chaperon_user_data")
        if (!raw) return null
        const user = JSON.parse(raw)
        return user?.id || null
    } catch (error) {
        return null
    }
}

const initialCreateForm = {
    title: "",
    place_label: "",
    description: "",
    cadence_mode: "fixed",
    cadence_minutes: 30,
    grace_minutes: 5,
    start_hours_offset: 1, // "Starts in 1 hour" by default, matching Android
    duration_hours: 4,
}

const initialEditForm = {
    title: "",
    cadence_mode: "fixed",
    cadence_minutes: 30,
    start_hours_offset: 0,
    duration_hours: 4,
}

/** now + hoursOffset, as an ISO string -- start_time for a new/edited trip. */
function hoursFromNowIso(hoursOffset) {
    return new Date(Date.now() + hoursOffset * 3_600_000).toISOString()
}

export default function Checker() {
    const communityManager = useCommunityManager()
    const [userId, setUserId] = useState(null)
    const [currentTrips, setCurrentTrips] = useState([])
    const [pastTrips, setPastTrips] = useState([])
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState("")
    // Bumped on every loadTrips() so each visible card's own companion
    // fetch (keyed on this) redoes itself too.
    const [refreshToken, setRefreshToken] = useState(0)

    const [isSubmitting, setIsSubmitting] = useState(false)
    const [actionTripId, setActionTripId] = useState(null)
    const [showCreateModal, setShowCreateModal] = useState(false)
    const [createForm, setCreateForm] = useState(initialCreateForm)
    const [createCompanions, setCreateCompanions] = useState([])
    const [createSelectedCompanionIds, setCreateSelectedCompanionIds] = useState(
        () => new Set(),
    )
    const [isLoadingCreateCompanions, setIsLoadingCreateCompanions] = useState(false)

    const [showEditModal, setShowEditModal] = useState(false)
    const [editingTripId, setEditingTripId] = useState(null)
    const [editForm, setEditForm] = useState(initialEditForm)
    // Snapshot of editForm as loaded, so handleEditTrip only sends the
    // fields the user actually changed, same as Android's parallel
    // original* vars.
    const [editOriginalForm, setEditOriginalForm] = useState(initialEditForm)
    const [editTripStatus, setEditTripStatus] = useState("")
    // "Starts in"/duration are only editable before the trip has started and
    // before any companion has accepted -- shifting either out from under an
    // accepted companion (who's already committed to watch at that time)
    // would be surprising, and the backend itself refuses a start_time
    // change once the trip is active. Matches Android's EditTripScreen.
    const [editCanEditStartTime, setEditCanEditStartTime] = useState(false)
    const [editCompanions, setEditCompanions] = useState([])
    const [editSelectedCompanionIds, setEditSelectedCompanionIds] = useState(
        () => new Set(),
    )
    const [editOriginalCompanionIds, setEditOriginalCompanionIds] = useState(
        () => new Set(),
    )
    const [isLoadingEditModal, setIsLoadingEditModal] = useState(false)
    const [editLoadFailed, setEditLoadFailed] = useState(false)
    const [showCancelOrDeleteDialog, setShowCancelOrDeleteDialog] = useState(false)
    const [isCancellingOrDeleting, setIsCancellingOrDeleting] = useState(false)

    const [showEventsModal, setShowEventsModal] = useState(false)
    const [events, setEvents] = useState([])
    const [isLoadingEvents, setIsLoadingEvents] = useState(false)
    const [eventsError, setEventsError] = useState("")
    const [eventsTripTitle, setEventsTripTitle] = useState("")

    useEffect(() => {
        setUserId(getUserIdFromStorage())
    }, [])

    const crumbs = useMemo(
        () => [
            { path: "/community", name: "Community" },
            { path: "/community/checker", name: "Checker" },
        ],
        [],
    )

    // One unfiltered fetch (same as the Android app's getTrips call), split
    // client-side into "current" (active/scheduled, sharing one card design)
    // and "past" -- rather than five separate status-filtered requests.
    const loadTrips = useCallback(async () => {
        if (!userId) return

        setIsLoading(true)
        setError("")

        try {
            const result = await getCheckerTrips({ user_id: userId, limit: 50 })
            const trips = toArray(result).map(normalizeCheckerTrip)

            const nextCurrent = trips.filter(
                (trip) =>
                    trip.display_status === "active" ||
                    trip.display_status === "scheduled",
            )
            const nextPast = trips.filter(
                (trip) =>
                    trip.display_status !== "active" &&
                    trip.display_status !== "scheduled",
            )

            nextPast.sort((a, b) => {
                const aDate =
                    parseApiDateTime(a?.updated_at || a?.created_at)?.getTime() ||
                    0
                const bDate =
                    parseApiDateTime(b?.updated_at || b?.created_at)?.getTime() ||
                    0
                return bDate - aDate
            })

            setCurrentTrips(nextCurrent)
            setPastTrips(nextPast)
            // Nudges every visible card's own companion fetch, since a
            // companion's response elsewhere isn't otherwise reflected here.
            setRefreshToken((token) => token + 1)
        } catch (requestError) {
            setError(requestError?.message || "Failed to load Checker trips")
        } finally {
            setIsLoading(false)
        }
    }, [userId])

    useEffect(() => {
        loadTrips()
    }, [loadTrips])

    // Catches up when the tab is shown again, similar in spirit to the
    // Android app's own resume-triggered refresh -- there's no push
    // notification channel on web to keep this current in the meantime.
    useEffect(() => {
        const onVisible = () => {
            if (document.visibilityState === "visible") loadTrips()
        }
        document.addEventListener("visibilitychange", onVisible)
        return () => document.removeEventListener("visibilitychange", onVisible)
    }, [loadTrips])

    const withTripAction = async (tripId, action) => {
        setActionTripId(tripId)
        setError("")

        try {
            await action()
            await loadTrips()
        } catch (requestError) {
            setError(requestError?.message || "Checker action failed")
        } finally {
            setActionTripId(null)
        }
    }

    const handleCheckin = async (trip, status) => {
        await withTripAction(trip.id, async () => {
            await submitCheckerCheckin(trip.id, { status })
        })
    }

    // Reopens the create form pre-filled from the old trip, rather than
    // calling the API's own /reuse endpoint directly (which the Android app
    // doesn't use either) -- lets the owner review/adjust before creating
    // what is otherwise a brand-new trip.
    const handleReuse = async (trip) => {
        setError("")

        // The old absolute start time is in the past by definition (this is
        // a past trip) -- start_hours_offset keeps its own "starts in 1
        // hour" default rather than reusing it. Duration is a real setting
        // worth carrying over, though.
        const startMillis = parseApiDateTime(trip.start_time)?.getTime()
        const endMillis = parseApiDateTime(trip.end_time)?.getTime()
        const durationHours =
            startMillis != null && endMillis != null
                ? Math.min(
                      48,
                      Math.max(
                          1,
                          Math.round((endMillis - startMillis) / 3_600_000),
                      ),
                  )
                : initialCreateForm.duration_hours

        setCreateForm({
            title: trip.title || "",
            place_label: trip.place_label || "",
            description: trip.description || "",
            cadence_mode: trip.cadence_mode || "fixed",
            cadence_minutes: trip.cadence_minutes || 30,
            grace_minutes: trip.grace_minutes || 5,
            start_hours_offset: initialCreateForm.start_hours_offset,
            duration_hours: durationHours,
        })
        setShowCreateModal(true)
        setIsLoadingCreateCompanions(true)

        try {
            const [availableCompanions, companions] = await Promise.all([
                getInvitableCompanions(userId),
                getCheckerCompanions(trip.id),
            ])

            const invitedIds = new Set(
                activeCheckerCompanions(companions).map((c) => String(c.user_id)),
            )
            setCreateCompanions(availableCompanions)
            setCreateSelectedCompanionIds(invitedIds)
        } catch (requestError) {
            console.error("Failed to load trip details to reuse:", requestError)
        } finally {
            setIsLoadingCreateCompanions(false)
        }
    }

    const handleCancel = async (trip) => {
        if (!window.confirm("Cancel this checker trip?")) return

        await withTripAction(trip.id, async () => {
            await cancelCheckerTrip(trip.id)
        })
    }

    const handleExtend = async (trip) => {
        // One cadence interval per tap, not a flat amount -- keeps "one more
        // check-in window" the mental model, matching the Android app.
        await withTripAction(trip.id, async () => {
            await extendCheckerTrip(trip.id, trip.cadence_minutes)
        })
    }

    // The card fires this itself the instant its own countdown to
    // start_time hits zero. Silent on failure -- this runs unattended, not
    // from a user tap, so an error banner here would be a surprise
    // interruption; the card just keeps showing "Overdue" until the next
    // loadTrips() (or the backend's own worker) catches it up.
    const handleAutoActivate = useCallback(
        async (tripId) => {
            try {
                await activateCheckerTrip(tripId)
                await loadTrips()
            } catch (requestError) {
                console.error(
                    `Auto-activate failed for trip ${tripId}:`,
                    requestError,
                )
            }
        },
        [loadTrips],
    )

    // Edits an already-created trip's title, cadence and invited companions
    // -- the fields it "makes sense" to change mid-trip. Place, start/end
    // time (once any companion has accepted) and grace window stay fixed,
    // matching the Android app's own EditTripScreen.
    const handleQuickEdit = async (tripId) => {
        setEditingTripId(tripId)
        setShowEditModal(true)
        setIsLoadingEditModal(true)
        setEditLoadFailed(false)

        try {
            const [trip, companions, availableCompanions] = await Promise.all([
                getCheckerTrip(tripId),
                getCheckerCompanions(tripId),
                getInvitableCompanions(userId),
            ])

            const invited = activeCheckerCompanions(companions)
            const invitedIds = new Set(invited.map((c) => String(c.user_id)))

            setEditTripStatus(trip.status || "")
            const nextForm = {
                title: trip.title || "",
                cadence_mode: trip.cadence_mode || "fixed",
                cadence_minutes: trip.cadence_minutes || 30,
                start_hours_offset: Math.min(
                    24,
                    Math.max(
                        0,
                        Math.round(
                            ((parseApiDateTime(trip.start_time)?.getTime() ?? Date.now()) -
                                Date.now()) /
                                3_600_000,
                        ),
                    ),
                ),
                duration_hours: (() => {
                    const startMillis = parseApiDateTime(trip.start_time)?.getTime()
                    const endMillis = parseApiDateTime(trip.end_time)?.getTime()
                    return startMillis != null && endMillis != null
                        ? Math.min(
                              48,
                              Math.max(
                                  1,
                                  Math.round((endMillis - startMillis) / 3_600_000),
                              ),
                          )
                        : initialEditForm.duration_hours
                })(),
            }
            setEditForm(nextForm)
            setEditOriginalForm(nextForm)
            // Only offered while there's nothing yet to disrupt by moving it.
            setEditCanEditStartTime(
                (trip.status === "scheduled" || trip.status === "draft") &&
                    !invited.some((c) => c.status === "accepted"),
            )

            // Union with already-invited companions who might not surface via
            // the community fan-out (e.g. no longer share a community with
            // the owner) -- otherwise they'd silently disappear from the
            // picker even though they're still selected.
            const alreadyListed = new Set(availableCompanions.map((c) => c.memberId))
            const missing = invited
                .filter((c) => !alreadyListed.has(String(c.user_id)))
                .map((c) => ({
                    memberId: String(c.user_id),
                    name:
                        [c.first_name, c.last_name].filter(Boolean).join(" ") ||
                        c.username ||
                        "Unknown",
                    initials: (c.first_name?.[0] || c.username?.[0] || "?").toUpperCase(),
                    profilePictureUrl: c.profile_picture_url || null,
                }))
            setEditCompanions(
                [...availableCompanions, ...missing].sort((a, b) =>
                    a.name.localeCompare(b.name),
                ),
            )
            setEditOriginalCompanionIds(invitedIds)
            setEditSelectedCompanionIds(invitedIds)
        } catch (requestError) {
            console.error(`Failed to load trip ${tripId} to edit:`, requestError)
            setEditLoadFailed(true)
        } finally {
            setIsLoadingEditModal(false)
        }
    }

    const handleShowEvents = async (trip) => {
        setShowEventsModal(true)
        setEvents([])
        setEventsError("")
        setEventsTripTitle(trip.title || "Checker trip")
        setIsLoadingEvents(true)

        try {
            const result = await getCheckerEvents(trip.id)
            const nextEvents = toArray(result)
            setEvents(nextEvents)
        } catch (requestError) {
            setEventsError(requestError?.message || "Failed to load events")
        } finally {
            setIsLoadingEvents(false)
        }
    }

    const handleCreateInput = (event) => {
        const { name, value } = event.target
        setCreateForm((prev) => ({ ...prev, [name]: value }))
    }

    const handleEditInput = (event) => {
        const { name, value } = event.target
        setEditForm((prev) => ({ ...prev, [name]: value }))
    }

    const setCreateField = (name) => (value) =>
        setCreateForm((prev) => ({ ...prev, [name]: value }))

    const setEditField = (name) => (value) =>
        setEditForm((prev) => ({ ...prev, [name]: value }))

    const toggleSetMember = (setState) => (memberId) =>
        setState((prev) => {
            const next = new Set(prev)
            if (next.has(memberId)) next.delete(memberId)
            else next.add(memberId)
            return next
        })
    const toggleCreateCompanion = toggleSetMember(setCreateSelectedCompanionIds)
    const toggleEditCompanion = toggleSetMember(setEditSelectedCompanionIds)

    const resetCreateForm = () => {
        setCreateForm(initialCreateForm)
        setCreateCompanions([])
        setCreateSelectedCompanionIds(new Set())
    }

    const resetEditForm = () => {
        setEditForm(initialEditForm)
        setEditOriginalForm(initialEditForm)
        setEditingTripId(null)
        setEditTripStatus("")
        setEditCanEditStartTime(false)
        setEditCompanions([])
        setEditSelectedCompanionIds(new Set())
        setEditOriginalCompanionIds(new Set())
        setEditLoadFailed(false)
        setShowCancelOrDeleteDialog(false)
    }

    // Fresh "+ New Checker Trip" open -- handleReuse (below) is the other,
    // already-populated way this modal opens.
    const handleOpenCreateModal = async () => {
        setError("")
        resetCreateForm()
        setShowCreateModal(true)
        setIsLoadingCreateCompanions(true)

        try {
            setCreateCompanions(await getInvitableCompanions(userId))
        } catch (requestError) {
            console.error("Failed to load community members:", requestError)
        } finally {
            setIsLoadingCreateCompanions(false)
        }
    }

    const handleCreateTrip = async (event) => {
        event.preventDefault()
        if (!userId) return

        if (!createForm.title.trim()) {
            setError("Please fill in the trip title")
            return
        }

        setIsSubmitting(true)
        setError("")

        try {
            const startTime = hoursFromNowIso(createForm.start_hours_offset)
            const endTime = hoursFromNowIso(
                createForm.start_hours_offset + createForm.duration_hours,
            )

            await createCheckerTrip({
                user_id: userId,
                title: createForm.title.trim(),
                description: createForm.description?.trim() || undefined,
                place_label: createForm.place_label?.trim() || undefined,
                cadence_mode: createForm.cadence_mode,
                cadence_minutes: Number(createForm.cadence_minutes),
                grace_minutes: Number(createForm.grace_minutes),
                start_time: startTime,
                end_time: endTime,
                companions: Array.from(createSelectedCompanionIds),
            })

            setShowCreateModal(false)
            resetCreateForm()
            await loadTrips()
        } catch (requestError) {
            setError(requestError?.message || "Failed to create checker trip")
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleEditTrip = async (event) => {
        event.preventDefault()
        if (!editingTripId) return

        const trimmedTitle = editForm.title.trim()
        if (!trimmedTitle) {
            setError("Please fill in the trip title")
            return
        }

        setIsSubmitting(true)
        setError("")

        try {
            const updates = {}
            if (trimmedTitle !== editOriginalForm.title) updates.title = trimmedTitle
            if (editForm.cadence_mode !== editOriginalForm.cadence_mode) {
                updates.cadence_mode = editForm.cadence_mode
            }
            if (Number(editForm.cadence_minutes) !== Number(editOriginalForm.cadence_minutes)) {
                updates.cadence_minutes = Number(editForm.cadence_minutes)
            }

            // "Starts in N hours" is always relative to right now, not to
            // whenever the trip was originally loaded, matching Android.
            if (
                editCanEditStartTime &&
                (editForm.start_hours_offset !== editOriginalForm.start_hours_offset ||
                    editForm.duration_hours !== editOriginalForm.duration_hours)
            ) {
                const startTime = hoursFromNowIso(editForm.start_hours_offset)
                updates.start_time = startTime
                updates.end_time = new Date(
                    new Date(startTime).getTime() + editForm.duration_hours * 3_600_000,
                ).toISOString()
            }

            const toAdd = [...editSelectedCompanionIds].filter(
                (id) => !editOriginalCompanionIds.has(id),
            )
            const toRemove = [...editOriginalCompanionIds].filter(
                (id) => !editSelectedCompanionIds.has(id),
            )

            if (Object.keys(updates).length === 0 && toAdd.length === 0 && toRemove.length === 0) {
                setShowEditModal(false)
                resetEditForm()
                setIsSubmitting(false)
                return
            }

            if (Object.keys(updates).length > 0) {
                await updateCheckerTrip(editingTripId, updates)
            }

            // Companion changes are separate endpoints (not part of PATCH),
            // so each add/remove is its own call -- best-effort per
            // companion so one failure doesn't block the others.
            let hadCompanionError = false
            await Promise.all([
                ...toAdd.map(async (id) => {
                    try {
                        await addCheckerCompanion(editingTripId, id)
                    } catch (requestError) {
                        hadCompanionError = true
                        console.error(`Failed to add companion ${id}:`, requestError)
                    }
                }),
                ...toRemove.map(async (id) => {
                    try {
                        await removeCheckerCompanion(editingTripId, id)
                    } catch (requestError) {
                        hadCompanionError = true
                        console.error(`Failed to remove companion ${id}:`, requestError)
                    }
                }),
            ])

            if (hadCompanionError) {
                setError("Trip updated, but some companion changes failed")
            }

            setShowEditModal(false)
            resetEditForm()
            await loadTrips()
        } catch (requestError) {
            setError(requestError?.message || "Failed to update checker trip")
        } finally {
            setIsSubmitting(false)
        }
    }

    // Scheduled/draft trips get deleted outright (no history yet worth
    // keeping); active trips only get cancelled -- matching the card's own
    // Cancel action and Android's EditTripScreen.
    const handleConfirmCancelOrDelete = async () => {
        if (!editingTripId) return

        setIsCancellingOrDeleting(true)
        setError("")

        try {
            if (editTripStatus === "active") {
                await cancelCheckerTrip(editingTripId)
            } else {
                await deleteCheckerTrip(editingTripId)
            }
            setShowCancelOrDeleteDialog(false)
            setShowEditModal(false)
            resetEditForm()
            await loadTrips()
        } catch (requestError) {
            const action = editTripStatus === "active" ? "cancel" : "delete"
            setError(requestError?.message || `Failed to ${action} trip`)
        } finally {
            setIsCancellingOrDeleting(false)
        }
    }

    return (
        <div className="checker-page">
            <Helmet>
                <title>Chaperon - Checker</title>
            </Helmet>

            <BreadCrumbs crumbs={crumbs} />

            <div className="container-fluid checker-container py-3">
                <div className="checker-header mb-4">
                    <h1 className="checker-title">Checker</h1>
                    <p className="checker-subtitle mb-0">
                        Invite Companions to monitor your safety during trips. Submit
                        heartbeats to confirm you&apos;re safe.
                    </p>
                </div>

                <div className="checker-layout">
                    <div className="checker-main">
                        {error && <div className="alert alert-danger">{error}</div>}

                        {isLoading && currentTrips.length === 0 && pastTrips.length === 0 ? (
                            <p className="text-muted">Loading Checker trips...</p>
                        ) : (
                            <>
                                {currentTrips.length > 0 && (
                                    <div className="checker-trip-list mb-4">
                                        {currentTrips.map((trip) => (
                                            <CheckerTripCard
                                                key={trip.id}
                                                trip={trip}
                                                refreshToken={refreshToken}
                                                isActionPending={actionTripId === trip.id}
                                                onEdit={() => handleQuickEdit(trip.id)}
                                                onAutoActivate={handleAutoActivate}
                                                onCheckin={(status) => handleCheckin(trip, status)}
                                                onExtend={() => handleExtend(trip)}
                                                onCancel={() => handleCancel(trip)}
                                                onShowEvents={() => handleShowEvents(trip)}
                                            />
                                        ))}
                                    </div>
                                )}

                                {/* Hidden entirely, not just disabled, once a trip is
                                    actually underway -- the API already rejects a
                                    second concurrent active trip, and there's
                                    nowhere to go create one from while that's true. */}
                                {!currentTrips.some((trip) => trip.display_status === "active") && (
                                    <button
                                        type="button"
                                        className="checker-new-trip-button mb-4"
                                        onClick={handleOpenCreateModal}
                                    >
                                        <i className="bi bi-plus-lg" aria-hidden="true"></i>
                                        New Checker Trip
                                    </button>
                                )}

                                {pastTrips.length > 0 && (
                                    <>
                                        <h2 className="checker-section-title">Past Trips</h2>
                                        <div className="checker-trip-list">
                                            {pastTrips.map((trip) => (
                                                <CheckerPastTripCard
                                                    key={trip.id}
                                                    trip={trip}
                                                    onReuse={() => handleReuse(trip)}
                                                    onShowEvents={() => handleShowEvents(trip)}
                                                />
                                            ))}
                                        </div>
                                    </>
                                )}

                                {currentTrips.length === 0 && pastTrips.length === 0 && (
                                    <div className="checker-empty-state">
                                        <img src={CHECKER_EMPTY_STATE_IMAGE_URL} alt="" />
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    <CommunityActivityColumn />
                </div>
            </div>

            <Modal
                open={showCreateModal}
                onClose={() => {
                    setShowCreateModal(false)
                    resetCreateForm()
                }}
                center
                classNames={{ modal: "checker-modal" }}
            >
                <h2 className="mb-3">New Checker Trip</h2>
                <form onSubmit={handleCreateTrip} className="checker-form">
                    <div>
                        <label htmlFor="title" className="form-label">
                            Trip Title (e.g. Commute to SFO)
                        </label>
                        <input
                            id="title"
                            name="title"
                            className="form-control"
                            value={createForm.title}
                            onChange={handleCreateInput}
                            required
                            maxLength={150}
                        />
                    </div>

                    <div>
                        <label htmlFor="description" className="form-label">
                            Description (Optional)
                        </label>
                        <textarea
                            id="description"
                            name="description"
                            className="form-control"
                            rows={2}
                            value={createForm.description}
                            onChange={handleCreateInput}
                        />
                    </div>

                    <div>
                        <label htmlFor="place_label" className="form-label">
                            Destination (Optional)
                        </label>
                        <CheckerPlacePickerField
                            id="place_label"
                            value={createForm.place_label}
                            onChange={setCreateField("place_label")}
                        />
                    </div>

                    <CheckerPresetRow
                        label="Check-in Cadence Interval"
                        options={CADENCE_PRESETS_CREATE}
                        value={createForm.cadence_minutes}
                        onChange={setCreateField("cadence_minutes")}
                    />

                    <CheckerPresetRow
                        label="Cadence Mode"
                        options={CADENCE_MODE_OPTIONS}
                        value={createForm.cadence_mode}
                        onChange={setCreateField("cadence_mode")}
                    />

                    <CheckerPresetRow
                        label="Late Grace Window Period"
                        options={GRACE_PRESETS}
                        value={createForm.grace_minutes}
                        onChange={setCreateField("grace_minutes")}
                    />

                    <CheckerHoursSlider
                        label="Starts in:"
                        value={createForm.start_hours_offset}
                        min={0}
                        max={24}
                        onChange={setCreateField("start_hours_offset")}
                        formatValue={(hours) => (hours === 0 ? "Now" : `${hours} hour(s)`)}
                    />
                    <CheckerHoursSlider
                        label="Total Trip Duration:"
                        value={createForm.duration_hours}
                        min={1}
                        max={48}
                        onChange={setCreateField("duration_hours")}
                        formatValue={(hours) => `${hours} hour(s)`}
                    />

                    <CheckerCompanionPicker
                        label="Invite Companions"
                        companions={createCompanions}
                        selectedIds={createSelectedCompanionIds}
                        onToggle={toggleCreateCompanion}
                        isLoading={isLoadingCreateCompanions}
                    />

                    <div className="checker-form-actions">
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => {
                                setShowCreateModal(false)
                                resetCreateForm()
                            }}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? "Creating..." : "Schedule Checker Trip"}
                        </button>
                    </div>
                </form>
            </Modal>

            <Modal
                open={showEditModal}
                onClose={() => {
                    setShowEditModal(false)
                    resetEditForm()
                }}
                center
                classNames={{ modal: "checker-modal" }}
            >
                <h2 className="mb-3">Edit Trip</h2>
                {isLoadingEditModal ? (
                    <p className="text-muted">Loading trip details...</p>
                ) : editLoadFailed ? (
                    <p className="text-danger">
                        Couldn&apos;t load this trip&apos;s details. Close and try again.
                    </p>
                ) : (
                    <form onSubmit={handleEditTrip} className="checker-form">
                        <div>
                            <label htmlFor="edit-title" className="form-label">
                                Trip Title
                            </label>
                            <input
                                id="edit-title"
                                name="title"
                                className="form-control"
                                value={editForm.title}
                                onChange={handleEditInput}
                                required
                                maxLength={150}
                            />
                        </div>

                        <CheckerPresetRow
                            label="Check-in Cadence Interval"
                            options={CADENCE_PRESETS_EDIT}
                            value={editForm.cadence_minutes}
                            onChange={setEditField("cadence_minutes")}
                        />

                        <CheckerPresetRow
                            label="Cadence Mode"
                            options={CADENCE_MODE_OPTIONS}
                            value={editForm.cadence_mode}
                            onChange={setEditField("cadence_mode")}
                        />

                        {/* Only offered while there's nothing yet to disrupt
                            by moving it -- see editCanEditStartTime's own
                            comment above. */}
                        {editCanEditStartTime && (
                            <>
                                <CheckerHoursSlider
                                    label="Starts in:"
                                    value={editForm.start_hours_offset}
                                    min={0}
                                    max={24}
                                    onChange={setEditField("start_hours_offset")}
                                    formatValue={(hours) =>
                                        hours === 0 ? "Now" : `${hours} hour(s)`
                                    }
                                />
                                <CheckerHoursSlider
                                    label="Total Trip Duration:"
                                    value={editForm.duration_hours}
                                    min={1}
                                    max={48}
                                    onChange={setEditField("duration_hours")}
                                    formatValue={(hours) => `${hours} hour(s)`}
                                />
                            </>
                        )}

                        <CheckerCompanionPicker
                            label="Invited Companions"
                            companions={editCompanions}
                            selectedIds={editSelectedCompanionIds}
                            onToggle={toggleEditCompanion}
                            isLoading={false}
                        />

                        <div className="checker-form-actions">
                            <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={() => {
                                    setShowEditModal(false)
                                    resetEditForm()
                                }}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? "Saving..." : "Save Changes"}
                            </button>
                        </div>

                        {/* Scheduled/draft trips get deleted outright (no
                            history yet worth keeping); active trips only
                            get cancelled -- matches the card's own Cancel
                            action. */}
                        {(editTripStatus === "active" ||
                            editTripStatus === "scheduled" ||
                            editTripStatus === "draft") && (
                            <button
                                type="button"
                                className="btn btn-link text-danger fw-bold"
                                onClick={() => setShowCancelOrDeleteDialog(true)}
                            >
                                {editTripStatus === "active"
                                    ? "Cancel Trip"
                                    : "Delete Trip"}
                            </button>
                        )}
                    </form>
                )}
            </Modal>

            <Modal
                open={showCancelOrDeleteDialog}
                onClose={() => {
                    if (!isCancellingOrDeleting) setShowCancelOrDeleteDialog(false)
                }}
                center
                classNames={{ modal: "checker-modal" }}
            >
                <h4 className="mb-3">
                    <i className="bi bi-exclamation-triangle text-warning pe-2"></i>
                    {editTripStatus === "active"
                        ? "Cancel this trip?"
                        : "Delete this trip?"}
                </h4>
                <p>
                    {editTripStatus === "active" ? (
                        <>
                            "{editOriginalForm.title}" is already underway --
                            this stops it and notifies you that it&apos;s
                            cancelled. This can&apos;t be undone.
                        </>
                    ) : (
                        <>
                            "{editOriginalForm.title}" hasn&apos;t started
                            yet. Deleting it removes it completely, including
                            any invited companions. This can&apos;t be undone.
                        </>
                    )}
                </p>
                <div className="d-flex gap-2 justify-content-end mt-4">
                    <button
                        type="button"
                        className="btn btn-danger"
                        onClick={handleConfirmCancelOrDelete}
                        disabled={isCancellingOrDeleting}
                    >
                        {isCancellingOrDeleting
                            ? "Working..."
                            : editTripStatus === "active"
                              ? "Cancel Trip"
                              : "Delete Trip"}
                    </button>
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setShowCancelOrDeleteDialog(false)}
                        disabled={isCancellingOrDeleting}
                    >
                        Never mind
                    </button>
                </div>
            </Modal>

            <Modal
                open={showEventsModal}
                onClose={() => setShowEventsModal(false)}
                center
                classNames={{ modal: "checker-modal" }}
            >
                <h2 className="mb-2">Timeline</h2>
                <p className="text-muted mb-3">{eventsTripTitle}</p>

                {isLoadingEvents ? (
                    <p className="text-muted">Loading timeline...</p>
                ) : eventsError ? (
                    <p className="text-danger">{eventsError}</p>
                ) : events.length === 0 ? (
                    <p className="text-muted">No events available for this trip yet.</p>
                ) : (
                    <ul className="checker-events-list">
                        {events.map((eventItem, index) => (
                            <li key={eventItem.id || `${eventItem.created_at}-${index}`}>
                                <div className="checker-event-top">
                                    <strong>{eventItem.summary || eventItem.event_type || "Event"}</strong>
                                    <span className={`checker-status-pill ${statusClass(eventItem.severity)}`}>
                                        {eventItem.severity || "okay"}
                                    </span>
                                </div>
                                <div className="checker-card-meta">
                                    {formatDateTime(eventItem.created_at)}
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </Modal>

            <CommunitySidebar manager={communityManager} />
        </div>
    )
}
